import React from 'react';
import clsx from 'clsx';
import { Portal } from '../Portal/Portal';
import { useBodyScrollLock } from '../../utils/useBodyScrollLock';
import { ReactComponent as CancelIcon } from 'common-assets/assets/icons/closeX.svg';
import { useFocusTrap } from '../../utils/useFocusTrap';
import { useTheme } from '../../styles/styles';
import { MobileAppTopBar } from '../../MobileAppTopBar/MobileAppTopBar';

import useStyles from './Modal.style';

export enum ModalType {
  withHeader = 'withHeader',
  menuModal = 'menuModal',
  modalCard = 'modalCard',
}

export interface ModalProps {
  handleClose: () => void;
  className?: string;
  headerClassName?: string;
  type?: ModalType;
  titleType?: 'center' | 'left';
  title?: string;
  withPortal?: boolean;
  closeButtonClassName?: string;
  bodyScrollLockDisabled?: boolean;
  modalContentId?: string;
  customZIndex?: number;
  isActiveFocusTrap?: boolean;
  mobileAppTopBarColor?: string;
}

const Modal: React.FC<ModalProps> = ({
  handleClose,
  children,
  className,
  headerClassName,
  title,
  type = ModalType.withHeader,
  customZIndex,
  titleType = 'center',
  withPortal = true,
  closeButtonClassName,
  bodyScrollLockDisabled = false,
  isActiveFocusTrap = true,
  modalContentId,
  mobileAppTopBarColor,
}) => {
  const c = useStyles({ type, titleType, customZIndex });
  const theme = useTheme();
  useBodyScrollLock(bodyScrollLockDisabled);
  const focusTrapContainerRef = useFocusTrap(handleClose, isActiveFocusTrap);
  const clsxTitleClassName = clsx(
    c.title,
    (titleType === 'center' && c['title--center']),
    (titleType === 'left' && c['title--left'])
  );

  const renderModalHeader = () => {
    switch (type) {

      case ModalType.withHeader:
        return (
          <header className={clsx(c.header, headerClassName)}>
            <span className={clsxTitleClassName}>{title}</span>
            <button onClick={handleClose} className={clsx(c.closeButton, closeButtonClassName)}>
              <CancelIcon />
            </button>
          </header>
        );

      case ModalType.menuModal:
        return (
          <>
            <header className={c.menuModal}>
              <button onClick={handleClose} className={clsx(c.closeButton, closeButtonClassName)}>
                <CancelIcon />
              </button>
              <span className={c.titleWithButtons}>{title}</span>
            </header>
          </>
        );

      case ModalType.modalCard:
      default:
        return null;
    }
  }

  return (
    <Wrapper withPortal={withPortal}>
      <MobileAppTopBar backgroundColor={mobileAppTopBarColor || theme.color.greys[0]} />
      <div className={clsx(c.root, className)} id={modalContentId} ref={focusTrapContainerRef}>
        {renderModalHeader()}
        {children}
      </div>
    </Wrapper>
  );
};

const Wrapper: React.FC<{ withPortal: boolean }> = ({ children, withPortal }) => withPortal ? <Portal id="root">{children}</Portal> : <>{children}</>;

export interface ModalWithLayoutProps extends ModalProps {
  customModalWithLayoutId?: string;
}

export const ModalWithLayout: React.FC<ModalWithLayoutProps> = ({
  children,
  customModalWithLayoutId,
  ...props
}) => {
  const c = useStyles({ type: props.type, titleType: props.titleType, customZIndex: props.customZIndex });
  const modalWithLayoutId = customModalWithLayoutId || 'modal-with-layout';

  return (
    <Modal
      {...props}
      bodyScrollLockDisabled
      handleClose={props.handleClose}
      type={ModalType.modalCard}
      className={clsx(c.modalWithLayout, props.className)}
      modalContentId={modalWithLayoutId}
      customZIndex={1001}
    >
      {children}
    </Modal>
  );
};

export default Modal;
